/* eslint-disable camelcase */
import { getCookie } from './actions/cookies_actions';

export interface TrackingParams {
  client_id: string;
  session_id: string;
  gclid: string;
  fbp: string;
  fbc: string;
  fbclid: string;
  event_id: string;
  page_location: string;
  user_agent: string;
}

interface CampaignParams {
  gclid: string;
  fbclid: string;
}

const STORAGE_KEY = 'cnl_campaign_params';
const TTL_MS = 90 * 24 * 60 * 60 * 1000;

const readQueryParam = (name: string): string => {
  try {
    return new URLSearchParams(window.location.search).get(name) ?? '';
  } catch {
    return '';
  }
};

const readStoredCampaignParams = (): CampaignParams => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { gclid: '', fbclid: '' };

    const parsed = JSON.parse(raw);
    if (!parsed || Date.now() - Number(parsed.savedAt) > TTL_MS) return { gclid: '', fbclid: '' };

    return { gclid: parsed.gclid ?? '', fbclid: parsed.fbclid ?? '' };
  } catch {
    return { gclid: '', fbclid: '' };
  }
};

/**
 * Persiste gclid/fbclid da URL atual. Só sobrescreve quando há valor novo — assim
 * uma navegação interna (sem query string) não apaga o que a landing capturou.
 */
export const captureCampaignParams = (): void => {
  try {
    const gclid = readQueryParam('gclid');
    const fbclid = readQueryParam('fbclid');

    if (!gclid && !fbclid) return;

    const stored = readStoredCampaignParams();

    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        gclid: gclid || stored.gclid,
        fbclid: fbclid || stored.fbclid,
        savedAt: Date.now(),
      }),
    );
  } catch {
    /* localStorage indisponível (modo privado / ITP) — seguimos sem persistência */
  }
};

/** Merge do que foi persistido com a URL atual — a URL tem precedência. */
export const readCampaignParams = (): CampaignParams => {
  const stored = readStoredCampaignParams();

  return {
    gclid: readQueryParam('gclid') || stored.gclid,
    fbclid: readQueryParam('fbclid') || stored.fbclid,
  };
};

/**
 * Query string com os parâmetros de campanha, para o `client.url_params` do wizard —
 * que hoje vai vazio por estar numa rota sem a query string original.
 */
export const campaignParamsToQueryString = (): string => {
  const { gclid, fbclid } = readCampaignParams();

  const parts: string[] = [];
  if (gclid) parts.push(`gclid=${encodeURIComponent(gclid)}`);
  if (fbclid) parts.push(`fbclid=${encodeURIComponent(fbclid)}`);

  return parts.join('&');
};

/**
 * client_id do GA4, extraído do cookie `_ga` (formato `GA1.1.<a>.<b>`).
 * O client_id é tudo depois do 2º ponto: `<a>.<b>`.
 */
const readGaClientId = (): string => {
  const raw = getCookie('_ga');
  if (!raw) return '';

  const parts = raw.split('.');
  return parts.length >= 4 ? parts.slice(2).join('.') : '';
};

/**
 * session_id do GA4, no cookie `_ga_<MEASUREMENT_ID>` (formato `GS1.1.<session_id>.<n>...`).
 * O nome do cookie depende do stream, então varremos por prefixo em vez de exigir
 * o Measurement ID configurado. O prefixo `GS1.1` varia conforme a versão do gtag —
 * por isso pegamos pela posição, não por literal.
 */
const readGaSessionId = (): string => {
  try {
    const cookie = document.cookie
      .split(';')
      .map((c) => c.trim())
      .find((c) => c.startsWith('_ga_'));

    if (!cookie) return '';

    const value = cookie.slice(cookie.indexOf('=') + 1);
    const parts = value.split('.');
    return parts.length >= 3 ? parts[2] : '';
  } catch {
    return '';
  }
};

/**
 * `_fbc` do Meta. Quando o cookie não existe (pixel ainda não rodou, ou bloqueado)
 * mas há `fbclid`, montamos no formato exigido pela CAPI: `fb.1.<timestamp>.<fbclid>`.
 */
const readFbc = (fbclid: string): string => {
  const cookie = getCookie('_fbc');
  if (cookie) return cookie;

  return fbclid ? `fb.1.${Date.now()}.${fbclid}` : '';
};

/**
 * event_id novo a cada submit — é a chave de deduplicação entre o evento do
 * browser (pixel/gtag) e o evento server-side. Nunca pode sair vazio.
 */
const generateEventId = (): string => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }

    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
      const bytes = crypto.getRandomValues(new Uint8Array(16));
      // eslint-disable-next-line no-bitwise
      bytes[6] = (bytes[6] & 0x0f) | 0x40; // versão 4
      // eslint-disable-next-line no-bitwise
      bytes[8] = (bytes[8] & 0x3f) | 0x80; // variante RFC 4122
      const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
      return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(
        16,
        20,
      )}-${hex.slice(20)}`;
    }
  } catch {
    /* cai no fallback abaixo */
  }

  return `${Date.now().toString(36)}-${Math.floor(Math.random() * 1e16).toString(36)}`;
};

const emptyTracking = (): TrackingParams => ({
  client_id: '',
  session_id: '',
  gclid: '',
  fbp: '',
  fbc: '',
  fbclid: '',
  event_id: generateEventId(),
  page_location: '',
  user_agent: '',
});

/** Monta o bloco `tracking` enviado no payload de cadastro. Chamar no submit. */
export const buildTracking = (): TrackingParams => {
  try {
    const { gclid, fbclid } = readCampaignParams();

    return {
      client_id: readGaClientId(),
      session_id: readGaSessionId(),
      gclid,
      fbp: getCookie('_fbp') ?? '',
      fbc: readFbc(fbclid),
      fbclid,
      event_id: generateEventId(),
      page_location: window.location.href,
      user_agent: navigator.userAgent,
    };
  } catch {
    return emptyTracking();
  }
};

export default buildTracking;
